import { useEffect, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { verifySignupOtpApi, resendSignupOtpApi, toAuthResult } from "@/portals/patient/api/patientAuthApi";
import { OtpPanel } from "./OtpPanel";
import { getAuthFlowState, updateAuthFlowState, clearAuthFlowState } from "./authFlowState";
import { savePendingClaimGuidance } from "./claimGuidance";
import { savePendingSsnPrompt } from "./ssnPrompt";
import { ClaimGuidanceCard } from "@/features/patient/family/components/ClaimGuidanceCard";

interface VerifyOtpState {
  email: string;
  fromSignup?: boolean;
  /** Set only when this signup started from a caregiver invite link (see
   * PatientSignUpPage) — used purely to tailor the post-verify notice; the
   * actual invite resolution already happened server-side inside verify-otp
   * itself (see PatientService.verify_otp), matching on the now-verified
   * email — this never needs to be sent back to the backend. */
  inviteContext?: { patientName: string; categories: string[] } | null;
}

export function VerifyOtpPage() {
  const navigate = useNavigate();
  const location = useLocation();
  // Set only while showing the one-time claim guidance card right after a
  // Dedicated Profile claim verifies — sign-in navigation waits for it to be
  // dismissed instead of firing immediately (see handleVerified/handleGuidanceDismissed).
  const [pendingGuidance, setPendingGuidance] = useState<{ creatorName: string | null } | null>(null);

  const routeState = location.state as VerifyOtpState | null;
  const flowState = getAuthFlowState();
  const email = routeState?.email ?? flowState?.email ?? null;
  const fromSignup = routeState?.fromSignup ?? flowState?.fromSignup ?? false;
  const inviteContext = routeState?.inviteContext ?? null;

  useEffect(() => {
    if (email) updateAuthFlowState(email, { fromSignup });
  }, [email, fromSignup]);

  if (!email) {
    return <Navigate to="/patient/auth/signin" replace />;
  }

  const goToSignin = () => {
    clearAuthFlowState();
    navigate("/patient/auth/signin", {
      replace: true,
      state: {
        notice: inviteContext
          ? `Email verified! You now have access to ${inviteContext.patientName}'s shared health records. Please sign in.`
          : "Email verified successfully! Please sign in.",
      },
    });
  };

  const handleVerified = (_otp: string, _resetToken?: string, wasClaim?: boolean, creatorName?: string | null) => {
    // The optional SSN step needs a signed-in account, so it waits for the
    // first Dashboard visit (see ssnPrompt.ts).
    if (fromSignup) savePendingSsnPrompt(email);
    if (wasClaim) {
      // Shown again once on the first Dashboard visit after signing in (see
      // claimGuidance.ts) — the same reminder from two different, natural
      // points in the journey, not two different messages.
      savePendingClaimGuidance({ email, creatorName: creatorName ?? null });
      setPendingGuidance({ creatorName: creatorName ?? null });
      return;
    }
    goToSignin();
  };

  if (pendingGuidance) {
    return <ClaimGuidanceCard creatorName={pendingGuidance.creatorName} onDismiss={goToSignin} />;
  }

  return (
    <OtpPanel
      email={email}
      title={<>Email <span className="font-extrabold tracking-tight">verification</span></>}
      subtitle="Verify to create an account or log in."
      onVerify={(otp) =>
        verifySignupOtpApi(email, otp).then((res) => ({
          ...toAuthResult(res),
          wasClaim: res.data?.wasClaim,
          creatorName: res.data?.creatorName,
        }))
      }
      onResend={() => resendSignupOtpApi(email).then(toAuthResult)}
      onVerified={handleVerified}
    />
  );
}
